import { Component } from '@theme/component';

const REFRESH_INTERVAL_MS = 1000;

/**
 * Countdown timer custom element.
 *
 * Reads `data-date-end` (required) and `data-date-start` (optional) as
 * strings parseable by `Date.parse`, evaluated in the visitor's local time
 * zone. While `now < date-start` it counts down to the start date; once
 * started it counts down to `date-end`; once that passes it stops ticking
 * and either swaps to the `expired` ref, or hides the enclosing section,
 * depending on `data-on-expire` (`show_message` | `hide_section`).
 *
 * @extends {Component<{units: HTMLElement, days: HTMLElement, hours: HTMLElement, minutes: HTMLElement, seconds: HTMLElement, expired: HTMLElement}>}
 */
class CountdownTimer extends Component {
  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    super.connectedCallback();
    this.#tick();
    this.#intervalId = window.setInterval(this.#tick, REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#intervalId);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#tick();
  };

  #tick = () => {
    const endDate = Date.parse(this.dataset.dateEnd ?? '');
    if (Number.isNaN(endDate)) return;

    const startDate = this.dataset.dateStart ? Date.parse(this.dataset.dateStart) : NaN;
    const now = Date.now();

    if (!Number.isNaN(startDate) && now < startDate) {
      this.#render(startDate - now);
      return;
    }

    const remaining = endDate - now;

    if (remaining <= 0) {
      this.#showExpired();
      return;
    }

    this.#render(remaining);
  };

  /** @param {number} remainingMs */
  #render(remainingMs) {
    const totalSeconds = Math.max(Math.floor(remainingMs / 1000), 0);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#setValue(this.refs.days, days);
    this.#setValue(this.refs.hours, hours);
    this.#setValue(this.refs.minutes, minutes);
    this.#setValue(this.refs.seconds, seconds);
  }

  /**
   * @param {HTMLElement | undefined} element
   * @param {number} value
   */
  #setValue(element, value) {
    if (!element) return;
    const formatted = String(value).padStart(2, '0');
    if (element.textContent !== formatted) element.textContent = formatted;
  }

  #showExpired() {
    window.clearInterval(this.#intervalId);

    if (this.dataset.onExpire === 'hide_section') {
      this.closest('.shopify-section')?.setAttribute('hidden', '');
      return;
    }

    this.refs.units?.setAttribute('hidden', '');
    this.refs.expired?.removeAttribute('hidden');
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
